/**
 * Журнал жизни (A8): новые записи сверху, год в моно слева; тяжёлые — год
 * кирпичным и блок помощи. Тап по строке — ☆ года, долгий тап — поле
 * «мысль» (до 100 знаков, личная, хранится в `params.thought`).
 * Показываются последние JOURNAL_PAGE записей, «раньше» открывает ещё.
 */
import { HELP_KEY, type Life, type LogEntry } from '@life/engine';
import { t } from '@life/i18n';
import { useState } from 'preact/hooks';
import { useLongPress } from '../lib/press';
import { HelpBlock } from './HelpBlock';

export const JOURNAL_PAGE = 30;
export const THOUGHT_MAX = 100;

export interface JournalProps {
  life: Life;
  onStar: (age: number) => void;
  onThought: (age: number, text: string) => void;
}

function thoughtOf(e: LogEntry): string | null {
  const v = e.params?.['thought'];
  return typeof v === 'string' && v !== '' ? v : null;
}

export function Journal({ life, onStar, onThought }: JournalProps) {
  const [shown, setShown] = useState(JOURNAL_PAGE);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const press = useLongPress();

  const total = life.log.length;
  /** Возраст записей последнего тика: прожитый год — `age - 1`, у умершего — год смерти. */
  const freshAge = life.alive ? life.age - 1 : life.age;
  const rows: { entry: LogEntry; index: number }[] = [];
  for (let i = total - 1; i >= Math.max(0, total - shown); i--) {
    const entry = life.log[i]!;
    // Запись `help.lines` движок пишет отдельной строкой после тяжёлой смерти;
    // блок помощи уже прикреплён к самой heavy-записи, повторять его нельзя.
    if (entry.key === HELP_KEY) continue;
    rows.push({ entry, index: i });
  }

  const openThought = (index: number, e: LogEntry) => {
    setEditing(index);
    setDraft(thoughtOf(e) ?? '');
  };
  const close = () => {
    setEditing(null);
    setDraft('');
  };

  return (
    <section class="journal">
      <div class="eyebrow">{t('ui.life.journal')}</div>
      <ul class="log">
        {rows.map(({ entry, index }) => {
          const fresh = entry.age === freshAge;
          const cls = [entry.heavy ? 'heavy' : '', fresh ? 'fresh' : ''].filter(Boolean).join(' ');
          const thought = thoughtOf(entry);
          const inputId = `thought-${index}`;
          return (
            <li key={index} class={cls}>
              <span class="y">{entry.age}</span>
              <div class="body">
                <button
                  type="button"
                  class="text"
                  {...press(
                    () => openThought(index, entry),
                    () => onStar(entry.age),
                  )}
                >
                  {entry.starred && <span class="star">{t('ui.life.star')} </span>}
                  {t(entry.key, entry.params)}
                </button>
                {thought && <span class="thought">{thought}</span>}
                {entry.heavy && <HelpBlock />}
                {editing === index && (
                  <form
                    class="thought-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      onThought(entry.age, draft.trim().slice(0, THOUGHT_MAX));
                      close();
                    }}
                  >
                    <label for={inputId}>{t('ui.life.thought')}</label>
                    <input
                      id={inputId}
                      type="text"
                      maxLength={THOUGHT_MAX}
                      value={draft}
                      onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
                    />
                    <p class="hint">{t('ui.life.thought.hint')}</p>
                    <div class="row">
                      <button type="submit" class="btn primary small">
                        {t('ui.life.thought.save')}
                      </button>
                      <button type="button" class="btn ghost small" onClick={close}>
                        {t('ui.life.thought.cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {shown < total && (
        <button type="button" class="btn ghost" onClick={() => setShown((s) => s + JOURNAL_PAGE)}>
          {t('ui.life.journal.earlier')}
        </button>
      )}
    </section>
  );
}
